import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';
import { WholeWord, Ruler } from 'lucide-react';

interface MessageComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string) => void;
  initialMessage?: string;
  isEditing?: boolean;
  isSaving?: boolean;
}

const MessageComposer = ({
  isOpen,
  onClose,
  onSubmit,
  initialMessage = '',
  isEditing = false,
  isSaving = false,
}: MessageComposerProps) => {
  const CHARACTER_LIMIT = 100;
  const LINE_LIMIT = 8;
  const { t } = useTranslation();
  const [message, setMessage] = useState(initialMessage);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Clamp initial value to the character limit when opening editor
    setMessage((initialMessage || '').slice(0, CHARACTER_LIMIT));
  }, [initialMessage, isOpen]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (message.trim()) {
      onSubmit(message);
      setMessage('');
    }
  };

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    
    // Check character limit
    if (newMessage.length > CHARACTER_LIMIT) {
      return;
    }
    
    // Check line limit
    const lines = newMessage.split('\n');
    if (lines.length > LINE_LIMIT) {
      return;
    }
    
    setMessage(newMessage);
  };

  const remainingChars = CHARACTER_LIMIT - message.length;
  const currentLines = message.split('\n').length;
  const remainingLines = LINE_LIMIT - currentLines + 1;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isEditing ? t('editMessage') : t('writeMessage')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Textarea
              ref={textareaRef}
              placeholder={t('writeMessagePlaceholder')}
              value={message}
              onChange={handleInputChange}
              maxLength={CHARACTER_LIMIT}
              className="min-h-24 resize-none rounded-lg border border-input bg-background p-3 text-lg focus:outline-none focus:ring-2 focus:ring-primary"
              style={{
                fontSize: '1.125rem', // >= 16px prevents iOS zoom
              }}
            />
          </div>
          <div className="flex justify-between items-center">
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <Ruler className="w-4 h-4 text-muted-foreground" style={{ transform: 'rotate(90deg)' }} />
                <p className={`text-xs font-medium ${remainingLines <= 0 ? 'text-destructive' : 'text-muted-foreground'
                  }`}>
                  {currentLines}/{LINE_LIMIT}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <WholeWord className="w-4 h-4 text-muted-foreground" />
              <p className={`text-xs ${remainingChars < 20 ? 'text-destructive font-semibold' : 'text-muted-foreground'
                }`}>
                {message.length}/{CHARACTER_LIMIT}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleSubmit}
            disabled={!message.trim() || isSaving}
            className="text-base py-2 px-4"
          >
            {isSaving ? t('savingMessage') : isEditing ? t('updateMessage') : t('sendMessage')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageComposer;
